/**
 * Owner site-content and media-library calls (contract v1.2 A.12, A.13).
 *
 * Owner-only, like adminService: the apiClient attaches the token and, on a
 * 401, clears it and fires 'hh:unauthorized'. Not exported from the public
 * services/api barrel.
 *
 * Validation errors carry `error.field` (a dotted path such as
 * 'data.schedule.end'); it arrives on the thrown ApiError as `field`, so a
 * form can put the message next to the right input.
 *
 * Every successful write forgets the public pages' content cache
 * (useContent), so a public page opened in the same tab afterwards shows
 * the change instead of what was loaded before it.
 *
 * @module contentAdminService
 */

import apiClient, { get, post, put, patch, del, unwrap } from './apiClient';
import { resetContentCache } from '../../hooks/useContent';

/** Resolve with the write's result after forgetting the public content cache. */
const written = async (promise) => {
  const result = await promise;
  resetContentCache();
  return result;
};

const contentPath = (collection, ...rest) =>
  ['/api/admin/content', encodeURIComponent(collection), ...rest.map((p) => encodeURIComponent(p))].join('/');

/* ------------------------------------------------------------------ content */

/** @returns {Promise<{collections: Array<{collection, items, visible, updated_at}>}>} */
export const getContentSummary = ({ signal } = {}) => get('/api/admin/content', { signal });

/**
 * All items of a collection (hidden ones included), by position; or, with
 * `deleted: true`, the deleted items, newest first.
 * @returns {Promise<{collection: string, items: object[]}>}
 */
export const listContent = (collection, { deleted = false, signal } = {}) =>
  get(contentPath(collection), { params: deleted ? { deleted: 'true' } : undefined, signal });

/**
 * Create an item. It is hidden unless `isVisible` is true, and goes last.
 * @param {string} collection
 * @param {{data: object, slug?: string, isVisible?: boolean}} item
 * @returns {Promise<object>} The item
 */
export const createContent = (collection, { data, slug, isVisible }) => {
  const body = { data };
  if (slug) body.slug = slug;
  if (typeof isVisible === 'boolean') body.is_visible = isVisible;
  return written(post(contentPath(collection), body));
};

/**
 * Replace an item's data (and slug). Pass the item's current `updated_at` as
 * `expectedUpdatedAt`: if it was edited elsewhere meanwhile, the call fails
 * with 409 content_conflict and nothing changes.
 * @returns {Promise<object>} The item
 */
export const updateContent = (collection, id, { data, slug, expectedUpdatedAt }) => {
  const body = { data };
  if (slug) body.slug = slug;
  if (expectedUpdatedAt) body.expected_updated_at = expectedUpdatedAt;
  return written(put(contentPath(collection, id), body));
};

/** Show or hide an item. @returns {Promise<object>} The item */
export const setContentVisible = (collection, id, isVisible) =>
  written(patch(contentPath(collection, id), { is_visible: Boolean(isVisible) }));

/**
 * New order: every item id of the collection exactly once.
 * @returns {Promise<{collection: string, items: object[]}>}
 */
export const reorderContent = (collection, ids) => written(post(contentPath(collection, 'reorder'), { ids }));

/** Soft delete (restorable). @returns {Promise<{id: number, deleted: true}>} */
export const deleteContent = (collection, id) => written(del(contentPath(collection, id)));

/** Bring a deleted item back, hidden, at the end. @returns {Promise<object>} The item */
export const undeleteContent = (collection, id) => written(post(contentPath(collection, id, 'undelete')));

/** Up to 20 revisions, newest first. @returns {Promise<{item_id: number, revisions: object[]}>} */
export const getContentRevisions = (collection, id, { signal } = {}) =>
  get(contentPath(collection, id, 'revisions'), { signal });

/** Put a revision's data and visibility back. @returns {Promise<object>} The item */
export const restoreContent = (collection, id, revisionId) =>
  written(post(contentPath(collection, id, 'restore'), { revision_id: revisionId }));

/* -------------------------------------------------------------------- media */

/** Largest upload the API accepts (A.13); check before sending to save her data plan. */
export const MAX_MEDIA_BYTES = 10 * 1024 * 1024;

/** Types the API accepts (decided from the bytes server-side; this is a first filter). */
export const ACCEPTED_MEDIA_TYPES = Object.freeze(['image/jpeg', 'image/png', 'image/webp', 'application/pdf']);

/**
 * The library, newest first, with usage and quota.
 * @param {{kind?: 'image'|'file', signal?: AbortSignal}} [options]
 * @returns {Promise<{media: object[], quota: {used_bytes: number, limit_bytes: number}}>}
 */
export const listMedia = ({ kind, signal } = {}) =>
  get('/api/admin/media', { params: kind ? { kind } : undefined, signal });

/**
 * Upload an image (alt text required) or a PDF.
 * @param {File|Blob} file
 * @param {{alt?: string, onProgress?: (fraction: number) => void, signal?: AbortSignal}} [options]
 * @returns {Promise<object>} The media object (+ size_bytes, created_at, used_by)
 * @throws {ApiError} 400 validation_error (field 'alt' or 'file'), 409 media_quota_exceeded,
 *   413 media_too_large, 415 media_type_not_allowed, 503 service_unavailable
 */
export const uploadMedia = async (file, { alt, onProgress, signal } = {}) => {
  const form = new FormData();
  form.append('file', file);
  if (alt != null) form.append('alt', alt);
  const response = await apiClient.post('/api/admin/media', form, {
    // multipart: axios lets the browser add the boundary. Leaving the
    // instance's JSON content type would serialise the form as JSON.
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000,
    signal,
    onUploadProgress: onProgress
      ? (event) => {
          if (event.total) onProgress(Math.min(1, event.loaded / event.total));
        }
      : undefined,
  });
  return unwrap(response);
};

/** Change an image's alt text. @returns {Promise<object>} The media object */
export const updateMediaAlt = (id, alt) => written(patch(`/api/admin/media/${encodeURIComponent(id)}`, { alt }));

/** Delete an unused asset. @throws {ApiError} 409 media_in_use (message names the items) */
export const deleteMedia = (id) => del(`/api/admin/media/${encodeURIComponent(id)}`);
